import React, { useState, useEffect } from "react";
import Navbar from "../Components/Navbar";
import { Axios } from "../config";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "../Style/Playing11.css";

const EMPTY_PLAYERS = Array(11).fill("");
const EMPTY_RESERVES = Array(2).fill("");

function toLocalDate(dateString) {
  const normalized = String(dateString).replace("T", " ").replace("Z", "").split(".")[0];
  const [datePart, timePart] = normalized.split(" ");
  const month = parseInt(datePart.split("-")[1], 10);
  const offset = month >= 4 && month <= 10 ? "-06:00" : "-07:00";
  return new Date(`${datePart}T${timePart}${offset}`);
}

function formatDate(dateString) {
  return toLocalDate(dateString).toLocaleDateString("en-CA", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "America/Edmonton",
  });
}

function formatTime(dateString) {
  return toLocalDate(dateString).toLocaleTimeString("en-CA", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "America/Edmonton",
  });
}

function formatReportingTime(dateString) {
  const gameDate = toLocalDate(dateString);
  const reportingDate = new Date(gameDate.getTime() - 60 * 60 * 1000);
  const time = reportingDate.toLocaleTimeString("en-CA", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "America/Edmonton",
  });
  const day = reportingDate.toLocaleDateString("en-CA", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "America/Edmonton",
  });
  return `${time} (${day})`;
}

function buildClipboardText(game, players, reserves) {
  const lines = [];
  lines.push(game.GameDetails.replace(/\s+playing\s+.*/i, "").trim());
  lines.push(`Format: ${game.Format}`);
  lines.push(`Reporting time: ${formatReportingTime(game.DateAndTime)}`);
  lines.push(`Ground: ${game.Venue}`);
  lines.push("");
  lines.push("Playing XI:");
  players.forEach((name, i) => {
    lines.push(`${i + 1}. ${name}`);
  });
  const filledReserves = reserves.filter((r) => r.trim());
  if (filledReserves.length > 0) {
    lines.push("");
    lines.push("Reserves:");
    filledReserves.forEach((name, i) => {
      lines.push(`${players.length + i + 1}. ${name}`);
    });
  }
  return lines.join("\n");
}

function Playing11() {
  const [games, setGames] = useState([]);
  const [selectedGameId, setSelectedGameId] = useState("");
  const [selectedGame, setSelectedGame] = useState(null);
  const [players, setPlayers] = useState(EMPTY_PLAYERS);
  const [reserves, setReserves] = useState(EMPTY_RESERVES);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Axios.post("/GetSeasonGamesData")
      .then((res) => {
        const now = new Date();
        const upcoming = res.data
          .filter((g) => toLocalDate(g.DateAndTime) >= now)
          .sort((a, b) => toLocalDate(a.DateAndTime) - toLocalDate(b.DateAndTime));
        setGames(upcoming);
      })
      .catch(() => setStatus("load_error"));
  }, []);

  const handleGameSelect = (gameId) => {
    setSelectedGameId(gameId);
    setStatus(null);
    if (!gameId) {
      setSelectedGame(null);
      setPlayers(EMPTY_PLAYERS);
      setReserves(EMPTY_RESERVES);
      return;
    }
    const game = games.find((g) => String(g.GameID) === String(gameId));
    setSelectedGame(game || null);

    Axios.get(`/playing11/${gameId}`)
      .then((res) => {
        const saved = res.data.playing11;
        if (saved) {
          setPlayers(saved.players || EMPTY_PLAYERS);
          setReserves(saved.reserves || EMPTY_RESERVES);
        } else {
          setPlayers(EMPTY_PLAYERS);
          setReserves(EMPTY_RESERVES);
        }
      })
      .catch(() => {
        setPlayers(EMPTY_PLAYERS);
        setReserves(EMPTY_RESERVES);
      });
  };

  const setPlayer = (index, value) => {
    setPlayers((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    setStatus(null);
  };

  const setReserve = (index, value) => {
    setReserves((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    setStatus(null);
  };

  const isValid = () => players.every((p) => p.trim().length > 0);

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }
    const el = document.createElement("textarea");
    el.value = text;
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
    return Promise.resolve();
  };

  const handleCopy = () => {
    if (!selectedGame || !isValid()) {
      setStatus("validation_error");
      return;
    }
    const text = buildClipboardText(selectedGame, players.map((p) => p.trim()), reserves.map((r) => r.trim()));
    copyToClipboard(text).then(() => setStatus("copied")).catch(() => setStatus("copy_error"));
  };

  const handleSaveAndCopy = async () => {
    if (!selectedGame || !isValid()) {
      setStatus("validation_error");
      return;
    }
    const text = buildClipboardText(selectedGame, players.map((p) => p.trim()), reserves.map((r) => r.trim()));
    copyToClipboard(text).catch(() => {});
    setSaving(true);
    setStatus(null);
    try {
      await Axios.post(`/playing11/${selectedGameId}`, {
        playing11: {
          players: players.map((p) => p.trim()),
          reserves: reserves.map((r) => r.trim()),
        },
      });
      setStatus("saved_and_copied");
    } catch {
      setStatus("save_error");
    } finally {
      setSaving(false);
    }
  };

  const previewText = selectedGame && isValid()
    ? buildClipboardText(selectedGame, players.map((p) => p.trim()), reserves.map((r) => r.trim()))
    : null;

  return (
    <>
      <Navbar />
      <div className="p11-page">
        <div className="p11-card">
          <div className="p11-icon">
            <i className="fas fa-cricket"></i>
          </div>
          <h1>Playing XI</h1>
          <p className="p11-subtitle">Select a game and fill in your squad.</p>

          <div className="p11-field">
            <label>Select Game <span className="req">*</span></label>
            <select
              value={selectedGameId}
              onChange={(e) => handleGameSelect(e.target.value)}
              className="p11-select"
            >
              <option value="">— Choose an upcoming game —</option>
              {games.map((g) => (
                <option key={g.GameID} value={g.GameID}>
                  {g.GameDetails} — {formatDate(g.DateAndTime)}
                </option>
              ))}
            </select>
          </div>

          {selectedGame && (
            <div className="p11-game-info">
              <div className="p11-game-title">{selectedGame.GameDetails}</div>
              <div className="p11-game-meta">
                <span><i className="fas fa-calendar-alt"></i> {formatDate(selectedGame.DateAndTime)}</span>
                <span><i className="fas fa-clock"></i> {formatTime(selectedGame.DateAndTime)}</span>
              </div>
              <div className="p11-game-meta">
                <span><i className="fas fa-map-marker-alt"></i> {selectedGame.Venue}</span>
              </div>
            </div>
          )}

          {selectedGame && (
            <>
              <div className="p11-section-label">Playing XI <span className="req">*</span></div>
              <div className="p11-players-grid">
                {players.map((name, i) => (
                  <div key={i} className="p11-player-row">
                    <span className="p11-num">{i + 1}.</span>
                    <input
                      type="text"
                      placeholder={`Player ${i + 1}`}
                      value={name}
                      onChange={(e) => setPlayer(i, e.target.value)}
                      className="p11-input"
                    />
                  </div>
                ))}
              </div>

              <div className="p11-section-label" style={{ marginTop: "24px" }}>
                Reserves <span className="p11-optional">(optional)</span>
              </div>
              <div className="p11-players-grid">
                {reserves.map((name, i) => (
                  <div key={i} className="p11-player-row">
                    <span className="p11-num">{players.length + i + 1}.</span>
                    <input
                      type="text"
                      placeholder={`Reserve ${i + 1}`}
                      value={name}
                      onChange={(e) => setReserve(i, e.target.value)}
                      className="p11-input"
                    />
                  </div>
                ))}
              </div>

              {status === "validation_error" && (
                <div className="p11-message error">
                  <i className="fas fa-exclamation-circle"></i>
                  Please fill in all 11 players before copying.
                </div>
              )}
              {status === "save_error" && (
                <div className="p11-message error">
                  <i className="fas fa-exclamation-circle"></i>
                  Failed to save. Check your connection and try again.
                </div>
              )}
              {status === "copy_error" && (
                <div className="p11-message error">
                  <i className="fas fa-exclamation-circle"></i>
                  Clipboard access denied. Please copy the preview manually.
                </div>
              )}
              {status === "copied" && (
                <div className="p11-message success">
                  <i className="fas fa-check-circle"></i>
                  Copied to clipboard!
                </div>
              )}
              {status === "saved_and_copied" && (
                <div className="p11-message success">
                  <i className="fas fa-check-circle"></i>
                  Saved and copied to clipboard!
                </div>
              )}

              <div className="p11-actions">
                <button
                  className="p11-btn primary"
                  onClick={handleSaveAndCopy}
                  disabled={saving}
                >
                  {saving ? (
                    <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                  ) : (
                    <><i className="fas fa-save"></i> Save &amp; Copy</>
                  )}
                </button>
                <button
                  className="p11-btn secondary"
                  onClick={handleCopy}
                  disabled={saving}
                >
                  <i className="fas fa-copy"></i> Copy Only
                </button>
              </div>

              {previewText && (
                <div className="p11-preview">
                  <div className="p11-preview-label">Preview</div>
                  <pre className="p11-preview-text">{previewText}</pre>
                </div>
              )}
            </>
          )}

          {status === "load_error" && (
            <div className="p11-message error" style={{ marginTop: "20px" }}>
              <i className="fas fa-exclamation-circle"></i>
              Failed to load games. Please refresh.
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Playing11;
